import logger from "@/utils/logger";
import { getDisplayName, isPrefixedField } from "@/utils/fields-mapping";

/**
 * Champs du schéma NormalizedModel et ses sous-modèles
 * Basé sur le schéma Pydantic fourni
 */

// Champs de Geolocation
export const GEOLOCATION_FIELDS = [
  "geolocation.latitude",
  "geolocation.longitude",
  "geolocation.address",
  "geolocation.region",
  "geolocation.country",
  "geolocation.city",
  "geolocation.id",
  "geolocation.title",
];

// Champs de Line (Transcription)
export const LINE_FIELDS = [
  "transcription.subtitles.start",
  "transcription.subtitles.end",
  "transcription.subtitles.text",
];

// Champs de Transcription
export const TRANSCRIPTION_FIELDS = [
  "transcription.language",
  "transcription.text",
  ...LINE_FIELDS,
];

// Champs de Stats
export const STATS_FIELDS = [
  "stats.likes",
  "stats.comments",
  "stats.shares",
  "stats.followers",
  "stats.following",
  "stats.posts",
  "stats.views",
  "stats.members",
];

// Champs principaux de NormalizedModel
// Note: author, parent, children, destination sont des objets NormalizedModel imbriqués
// Ils peuvent avoir leurs propres sous-champs (id, username, date, etc.)
export const NORMALIZED_MODEL_FIELDS = [
  "id",
  "username",
  "date",
  "title",
  "description",
  "text",
  "media",
  "url",
  "language",
  "verified",
  "duration",
  "type",
  // Champs imbriqués pour author, parent, children, destination
  "author",
  "author.id",
  "author.username",
  "author.date",
  "author.title",
  "parent",
  "parent.id",
  "parent.username",
  "parent.date",
  "children",
  "destination",
  "destination.id",
  "destination.username",
  "destination.date",
];

// Tous les champs prioritaires du schéma NormalizedModel
export const PRIORITY_FIELDS = [
  ...NORMALIZED_MODEL_FIELDS,
  ...STATS_FIELDS,
  ...GEOLOCATION_FIELDS,
  ...TRANSCRIPTION_FIELDS,
];

/**
 * Vérifie si un champ fait partie des champs prioritaires du schéma NormalizedModel
 */
export const isPriorityField = (fieldKey: string): boolean => {
  if (!fieldKey) return false;

  // Si le champ est au format type$name (ex: "num$likes"), extraire le nom d'affichage
  let normalizedKey = fieldKey;
  if (isPrefixedField(fieldKey)) {
    normalizedKey = getDisplayName(fieldKey);
  }

  // Normaliser le champ (enlever .keyword à la fin et retirer normalized. s'il existe)
  normalizedKey = normalizedKey.replace(/\.keyword$/, "");

  // Retirer le préfixe "normalized." s'il existe (pour compatibilité)
  if (normalizedKey.startsWith("normalized.")) {
    normalizedKey = normalizedKey.substring("normalized.".length);
  }

  // Vérifier les correspondances exactes avec les champs prioritaires
  if (PRIORITY_FIELDS.includes(normalizedKey)) {
    return true;
  }

  // Patterns de champs du schéma NormalizedModel
  const normalizedPatterns = [
    // Champs directs
    /^(id|username|date|title|description|text|media|url|language|verified|duration|type)(\.keyword)?$/,
    // Stats
    /^stats\.(likes|comments|shares|followers|following|posts|views|members)(\.keyword)?$/,
    // Geolocation
    /^geolocation\.(latitude|longitude|address|region|country|city|id|title)(\.keyword)?$/,
    // Transcription
    /^transcription\.(language|text|subtitles\.(start|end|text))(\.keyword)?$/,
    // Author, parent, children, destination (objets imbriqués)
    /^(author|parent|children|destination)(\.(id|username|date|title))?(\.keyword)?$/,
  ];

  // Vérifier si le champ correspond à un pattern
  for (const pattern of normalizedPatterns) {
    if (pattern.test(normalizedKey)) {
      return true;
    }
  }

  // Vérifier si le champ correspond à un champ prioritaire listé
  return PRIORITY_FIELDS.some((priorityField) => {
    // Correspondance exacte
    if (normalizedKey === priorityField) {
      return true;
    }

    // Correspondance pour les champs imbriqués directs (sans sous-champs supplémentaires)
    if (normalizedKey.startsWith(priorityField + ".")) {
      const remaining = normalizedKey.substring(priorityField.length + 1);
      // Si le reste ne contient qu'un seul niveau (ex: "keyword"), c'est OK
      if (!remaining.includes(".")) {
        return true;
      }
    }

    return false;
  });
};

/**
 * Organise les champs en deux groupes: prioritaires et autres
 */
export const organizeFieldsByPriority = <T extends { key: string }>(
  fields: T[]
): {
  priorityFields: T[];
  otherFields: T[];
} => {
  const priorityFields: T[] = [];
  const otherFields: T[] = [];

  fields.forEach((field) => {
    if (isPriorityField(field.key)) {
      priorityFields.push(field);
    } else {
      otherFields.push(field);
    }
  });

  // Debug: afficher quelques exemples de détection
  if (priorityFields.length > 0 || otherFields.length > 0) {
    logger.debug("organizeFieldsByPriority - Debug:", {
      total: fields.length,
      priority: priorityFields.length,
      other: otherFields.length,
      samplePriority: priorityFields.slice(0, 5).map((f) => f.key),
      sampleOther: otherFields.slice(0, 5).map((f) => f.key),
    });
  }

  return { priorityFields, otherFields };
};

/**
 * Mapping from display field names to their ES-prefixed keys and types.
 * Keys must match the format returned by the /workspaces/fields API (e.g. "text$text", "num$likes").
 */
const DEFAULT_FIELD_DEFINITIONS: Array<{ key: string; type: string }> = [
  { key: "text$id", type: "text" },
  { key: "text$username", type: "text" },
  { key: "date$date", type: "date" },
  { key: "text$title", type: "text" },
  { key: "text$description", type: "text" },
  { key: "text$text", type: "text" },
  { key: "text$media", type: "text" },
  { key: "text$url", type: "text" },
  { key: "text$language", type: "text" },
  { key: "bool$verified", type: "boolean" },
  { key: "num$duration", type: "long" },
  { key: "text$type", type: "text" },
  { key: "obj$stats.num$likes", type: "long" },
  { key: "obj$stats.num$comments", type: "long" },
  { key: "obj$stats.num$shares", type: "long" },
  { key: "obj$stats.num$followers", type: "long" },
  { key: "obj$stats.num$following", type: "long" },
  { key: "obj$stats.num$posts", type: "long" },
  { key: "obj$stats.num$views", type: "long" },
  { key: "obj$stats.num$members", type: "long" },
  { key: "obj$geolocation.num$latitude", type: "float" },
  { key: "obj$geolocation.num$longitude", type: "float" },
  { key: "obj$geolocation.text$address", type: "text" },
  { key: "obj$geolocation.text$region", type: "text" },
  { key: "obj$geolocation.text$country", type: "text" },
  { key: "obj$geolocation.text$city", type: "text" },
  { key: "obj$geolocation.text$id", type: "text" },
  { key: "obj$geolocation.text$title", type: "text" },
  { key: "obj$transcription.text$language", type: "text" },
  { key: "obj$transcription.text$text", type: "text" },
  { key: "obj$transcription.obj$subtitles.num$start", type: "float" },
  { key: "obj$transcription.obj$subtitles.num$end", type: "float" },
  { key: "obj$transcription.obj$subtitles.text$text", type: "text" },
  { key: "obj$author.text$id", type: "text" },
  { key: "obj$author.text$username", type: "text" },
  { key: "obj$author.date$date", type: "date" },
  { key: "obj$author.text$title", type: "text" },
  { key: "obj$parent.text$id", type: "text" },
  { key: "obj$parent.text$username", type: "text" },
  { key: "obj$parent.date$date", type: "date" },
  { key: "obj$destination.text$id", type: "text" },
  { key: "obj$destination.text$username", type: "text" },
  { key: "obj$destination.date$date", type: "date" },
];

/**
 * Génère les champs normalisés par défaut (type DOC) avec leurs types Elasticsearch
 * Ces champs sont toujours disponibles même sans mapping ou facets
 */
export const getDefaultNormalizedFields = (): Array<{
  key: string;
  type: string;
  enabled: boolean;
}> => {
  return DEFAULT_FIELD_DEFINITIONS.map(({ key, type }) => ({
    key,
    type,
    enabled: true,
  }));
};
